import { salesPriceConditionSchema } from '@retail/calculus/components';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { ConditionsNew } from '@retail/calculus/types';
import { UniversalFormProvider } from '@shared/components';
import { useMemo } from 'react';
import { EditSalesPriceConditionDialog } from './EditSalesPriceConditionDialog';
import { useFetchPriceSuggestionTypesWithCount } from '@retail/price-suggestion/data-access';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { getAvailableTypes } from '@retail/price-suggestion/utils';

interface Props {
  open: boolean;
  assortmentId: number;
  condition: ConditionsNew;
  supplierId?: number;
  includeMarkup: boolean;
  onClose: () => void;
}

function EditSalesPriceConditionDialogContainer({
  open,
  assortmentId,
  condition,
  supplierId,
  includeMarkup,
  onClose,
}: Props) {
  const t = useCalculusTFunction();
  const { selectedOrgUnitAssortment, appTenant, orgUnitType } = useSelectedOrgUnit();
  const localPriceSuggestionIsEnabled = useIsFeatureEnabled('local-price-suggestions');

  const { userCanRead } = usePermissionsForCurrentUser();
  const userCanReadLocalPriceSuggetions =
    userCanRead('PRICE_SUGGESTIONS') && localPriceSuggestionIsEnabled;

  const currentPriority = useMemo(
    () => condition.priceSourceCondition?.priorities.map((prio) => prio.priceSuggestionType) ?? [],
    [condition.priceSourceCondition?.priorities]
  );

  const defaultValues = useMemo(() => {
    return {
      markupCoverageDegree: condition.markupCondition?.isInherited
        ? undefined
        : condition.markupCondition?.coverageDegree,
      priceSourcePriority: condition.markupCondition?.isInherited
        ? currentPriority.filter((prio) => prio !== 'MARKUP')
        : currentPriority,
    };
  }, [
    condition.markupCondition?.coverageDegree,
    condition.markupCondition?.isInherited,
    currentPriority,
  ]);

  const isProductGroupCondition = ['OVERGRUPPE', 'HOVEDGRUPPE', 'VAREGRUPPE'].includes(
    condition.bindingType
  );

  const { data: priceSuggestionTypesWithCount = [], isLoading: isLoadingFetchPriceSuggestions } =
    useFetchPriceSuggestionTypesWithCount(
      {
        assortmentId,
        mgSupplierId: condition.supplier?.supplierNumber,
        productGroup: isProductGroupCondition ? condition.bindingTargetId : undefined,
        mgItemNumber: condition.bindingTargetItem?.mgItemNumber,
      },
      { suspense: false }
    );

  const filteredPriceSuggestions = useMemo(() => {
    const availableTypes = getAvailableTypes({
      countryCode: appTenant.countryCode,
      orgUnitType,
      canReadLocal: userCanReadLocalPriceSuggetions,
      assortmentLevel: condition.bindingType === 'ASSORTMENT',
    });
    return priceSuggestionTypesWithCount.filter((type) =>
      availableTypes.includes(type.suggestionType)
    );
  }, [
    priceSuggestionTypesWithCount,
    appTenant.countryCode,
    orgUnitType,
    userCanReadLocalPriceSuggetions,
    condition.bindingType,
  ]);

  return (
    <UniversalFormProvider schema={salesPriceConditionSchema(t)} defaultValues={defaultValues}>
      <EditSalesPriceConditionDialog
        open={open}
        includeMarkup={includeMarkup}
        currentPriceSuggestionPriority={currentPriority ?? []}
        salesPriceCondition={condition}
        availablePriceSuggestionTypes={filteredPriceSuggestions}
        levelName={
          supplierId || isProductGroupCondition
            ? condition.bindingTargetName ?? ''
            : selectedOrgUnitAssortment.name
        }
        isLoadingFetchPriceSuggestions={isLoadingFetchPriceSuggestions}
        onClose={onClose}
      />
    </UniversalFormProvider>
  );
}
export default withDialogSuspenseErrorBoundary(EditSalesPriceConditionDialogContainer);
